import * as assert from 'assert';
import { computed, observable } from 'mobx';
import { IAuthUser, ILoginResponse } from '../_interfaces/ServerResponses';
import { AuthService } from '../_services/AuthService';
import { ISetupApplicationStore } from './ApplicationStore';

export interface ISignUpParams {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    invite_key: string;
}

export interface IFacebookUserDetails {
  userID: string
  accessToken: string
  email: string
}

export function isSuccessfulFacebookAuthResponse(response: any) {
    return response && response.userID && response.accessToken && response.email;
}

const AUTH_TOKEN_STORAGE_KEY = '@tightknit:auth_token';

export class AuthorizationStore {
    private _applicationStore: ISetupApplicationStore;
    private _authService: AuthService;
    @observable
    private _isAuthenticated: boolean;
    @observable
    private _user?: IAuthUser;
    @observable
    private _token?: string;
    @observable
    private _authIsResolved: boolean;
    @observable
    private _isNewUser?: boolean;

    public constructor(applicationStore: ISetupApplicationStore) {
        this._isAuthenticated = false;
        this._authIsResolved = false;
        this._applicationStore = applicationStore;
        this._authService = new AuthService();
        this._isNewUser = false;
        this.startAuthListener();
    }

    @computed
    public get isAuthenticated(): boolean {
        return this._isAuthenticated;
    }

    @computed
    public get authIsResolved(): boolean {
        return this._authIsResolved;
    }

    @computed
    public get isNewUser(): boolean {
        return this._isNewUser || false;
    }

    @computed
    public get user(): IAuthUser {
        assert(this._user, 'called get user before it was available');
        if (this._user) {
            return this._user;
        } else {
            throw new Error('called get user before it was available');
        }
    }

    @computed
    public get authToken(): string {
        assert(this._token, 'called get authToken before it was available');
        if (this._token) {
            return this._token;
        } else {
            throw new Error('called get authToken before it was available');
        }
    }

    public refreshAuthToken(): void {
        this._authService.checkToken(this._token || '');
    }

    public startAuthListener(): void {
        const token = this.getStoredAuthToken();
        if (token) {
            this._authService
                .checkToken(token)
                .then((response: ILoginResponse) => {
                    this._authCallback(response);
                })
                .catch(() => {
                    this._authIsResolved = true;
                    this._isAuthenticated = false;
                    this.logout();
                });
        } else {
            this._authIsResolved = true;
        }
    }

    public checkUserExists(email: string) {
        return this._authService.checkUserExists(email);
    }

    public loginUserWithToken(response: ILoginResponse): Promise<void> {
        return this._authCallback(response);
    }

    private async _authCallback(response: ILoginResponse): Promise<void> {
        console.assert(response, 'Paramater is undefined');

        if (response && response.user && response.token) {
            this._token = response.token; // TODO save token in persistent storage
            this._isNewUser = response.isNewUser;
            this.saveAuthToken(this._token);
            this._handleAuthenticatedUser(response.user);
        } else {
            this._handleUnauthenticatedUser();
        }

        return Promise.resolve();
    }

    private _handleAuthenticatedUser(user: IAuthUser): void {
        console.assert(user, 'Paramater is undefined');

        this._isAuthenticated = true;
        this._user = user;
        this._authIsResolved = true;
        this._applicationStore.initializeStore();
    }

    private _handleUnauthenticatedUser(): void {
        this.logout();
        this._authIsResolved = true;
    }

    public async loginWithFacebook(facebookUserDetails: IFacebookUserDetails) {
      const result = await this._authService.loginWithFacebook(facebookUserDetails.email, facebookUserDetails.accessToken, facebookUserDetails.userID);
      await this._authCallback(result);
    }

    public login(email: string, password: string): Promise<void> {
        console.assert(email, 'Paramater is undefined');
        console.assert(password, 'Paramater is undefined');

        return this._authService.login(email, password).then((response: ILoginResponse) => {
            return this._authCallback(response);
        });
    }

    public signUp(params: ISignUpParams): Promise<ILoginResponse> {
        console.assert(params, 'Paramater is undefined');

        return this._authService.signUp(params).then((response: ILoginResponse) => {
            this._authCallback(response);
            return response;
        });
    }

    public logout(): void {
        this._isAuthenticated = false;
        this.clearAuthToken();
    }

    private saveAuthToken(token: string): void {
        console.assert(token, 'Paramater is undefined');

        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    }

    private clearAuthToken(): void {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }

    private getStoredAuthToken(): string | undefined {
        const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        if (typeof token === 'string') {
            return token;
        } else {
            return;
        }
    }
}



// WEBPACK FOOTER //
// ./src/_stores/AuthorizationStore.ts